<template>
  <div class="relative min-h-screen min-w-screen flex flex-col justify-between">
      <pulse-loader :loading="loading" color="#223672" size="15px" class="fixed transform -translate-y-1/2 -translate-x-1/2 left-1/2 top-1/2"></pulse-loader>

      <div class="" :class="editingState ? 'pb-20' : '' ">
          <router-link 
            :to="{ name: 'Dashboard'}"
            class="absolute top-10 left-8"
          >
              <svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mt-1.5"><path d="M1 8L17 8M8 1L1 8L8 15" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </router-link>
          <default-header 
              type="light"
              headerClasses=""
              textClasses="text-white"
              :gradientColour="currentPassBGColour"
              :masked="false"
          />
          <div class="-mt-12 py-6 text-white" v-if="!loading" :style="currentPassBGColour ? `background-image: linear-gradient(${currentPassBGColour})` : '' ">
              <div class="px-6 space-y-2">
                  <div v-if="!editingState" class="flex items-center space-x-2.5 pb-6">
                      <span class="block text-lg font-sans-secondary leading-tight">{{ pass.firstName && pass.lastName ? pass.firstName + ' ' + pass.lastName : ( pass.firstName ? pass.firstName : ( pass.lastName ? pass.lastName : $t('username') ) ) }}</span>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="fill-current" @click="toggleEditingState()"><g id="edit_icon"><rect id="Rectangle" opacity="0.01" width="24" height="24" fill="white"/><g id="Icon"><path id="Shape" d="M15.5903 4.90087L14.8832 4.19377L14.8832 4.19377L15.5903 4.90087ZM19.0536 8.40934L19.7514 9.12558L19.7514 9.12557L19.0536 8.40934ZM4.4933 15.9979L3.78619 15.2908L3.78617 15.2908L4.4933 15.9979ZM4 20H3C3 20.5523 3.44772 21 4 21V20ZM7.6484 19.5221L6.95054 18.8059L6.95053 18.8059L7.6484 19.5221ZM14.5317 7.37374L16.2974 5.60797L14.8832 4.19377L13.1175 5.95955L14.5317 7.37374ZM16.2974 5.60797C16.8695 5.03587 17.7971 5.03587 18.3692 5.60797L19.7834 4.19377C18.4303 2.8406 16.2363 2.8406 14.8832 4.19377L16.2974 5.60797ZM18.3692 5.608C18.9466 6.18535 18.9405 7.12329 18.3557 7.69312L19.7514 9.12557C21.1346 7.77783 21.1491 5.55935 19.7834 4.19374L18.3692 5.608ZM18.3557 7.69311L16.6354 9.36922L18.0312 10.8017L19.7514 9.12558L18.3557 7.69311ZM13.1175 5.95954L3.78619 15.2908L5.2004 16.705L14.5317 7.37376L13.1175 5.95954ZM3.78617 15.2908C3.28279 15.7942 3 16.4769 3 17.1889H5C5 17.0073 5.07209 16.8333 5.20042 16.705L3.78617 15.2908ZM3 17.1889V20H5V17.1889H3ZM4 21H6.47305V19H4V21ZM6.47305 21C7.17282 21 7.84499 20.7267 8.34627 20.2383L6.95053 18.8059C6.82281 18.9303 6.6515 19 6.47305 19V21ZM8.34627 20.2383L18.0312 10.8017L16.6354 9.36922L6.95054 18.8059L8.34627 20.2383ZM13.1267 7.38287L16.6354 10.8017L18.0312 9.36923L14.5225 5.95042L13.1267 7.38287Z" fill=""/></g></g></svg>
                  </div>
                  <div v-else class="flex flex-wrap items-center justify-between space-y-5 sm:space-y-0 sm:space-x-5 pb-6">
                      <div class="w-full sm:w-3/4" :style="`color: ${currentPassFontColour}`">
                          <input type="text" v-model="passName" class="rounded border border-gray-200 p-4 text-lg w-full font-sans-secondary" ref="passName" />
                      </div>
                      <div class="flex items-center justify-between w-full sm:w-1/4">
                          <div class="flex items-center space-x-1 w-full">
                              <div class="colourOptions p-3 rounded-full border-2" 
                                @click="changeThemeColour(colour)"
                                v-for="(colour, index) in selectableColours" :key="index"
                                :class="passObject.deviceColour == colour.title ? 'border-white' : 'border-gray-300'" 
                                :style="colour.bgColour ? `background-image: linear-gradient(${colour.bgColour})` : '' "
                              ></div>
                          </div>
                          <button class="text-xs flex items-center space-x-2.5 min-w-max border border-white bg-white rounded-sm p-1 px-2" @click="updateLifePass">
                            <span class="block" :style="`color: ${currentPassFontColour}`">{{ $t('updatelifepass') }}</span>
                            <svg width="21" height="22" viewBox="0 0 21 22" fill="none" xmlns="http://www.w3.org/2000/svg" class="stroke-current h-3.5 w-3.5" :style="`color: ${currentPassFontColour}`"><path d="M3.5 11L8.75 16.25L17.5 5.75" stroke="" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>
                          </button>
                      </div>
                  </div>
                  <div class="grid grid-cols-4 gap-5 xs:gap-10">
                      <div class="flex flex-col col-span-2">
                          <span class="block text-base">{{ $t('lastupdated') }}</span>
                          <span class="block text-md font-sans-secondary leading-tight">{{ timeAgo(pass.lastComm) }} ago</span>
                      </div>
                      <div class="flex flex-col col-span-2 xs:col-span-1">
                          <span class="block text-base">LifePass</span>
                          <span class="block text-md font-sans-secondary leading-tight">{{ passNo }} {{ $t('of') }} {{ passesTotal }}</span>
                      </div>
                      <div class="flex flex-col col-span-3 xs:col-span-1">
                          <span class="block text-base">{{ $t('battery') }}</span>
                          <battery-status :charge="pass.battery" />
                      </div>
                  </div>
              </div>
          </div>
          <div class="additionalPassDetails space-y-6 px-6 py-10" :style="`color: ${currentPassFontColour}`">
              <div class="flex items-center flex-wrap space-y-5 xs:space-y-0 xs:space-x-8 border-b border-gray-200 pb-4" v-if="!loading">
                  <div class="flex flex-col mr-8 xs:mr-0">
                      <span class="block text-sm text-primary">{{ $t('lifepassexpires') }}</span>
                      <span class="block font-sans-secondary text-mmd">{{ formatDate(pass.allocatedTo) }}</span>
                  </div>
                  <div class="flex flex-col">
                      <span class="block text-sm text-primary">LifePass ID</span>
                      <span class="block font-sans-secondary text-mmd">{{ pass.deviceCode }}</span>
                  </div>
              </div>
              <div class="grid grid-cols-2 xs:grid-cols-3 gap-5 sm:gap-8" v-if="!loading">
                  <div class="flex flex-col xs:mr-0">
                      <span class="block text-sm text-primary">{{ $t('latitude') }}</span>
                      <span class="block font-sans-secondary text-mmd">{{ pass.latitude }}</span>
                  </div>
                  <div class="flex flex-col xs:mr-0">
                      <span class="block text-sm text-primary">{{ $t('longitude') }}</span>
                      <span class="block font-sans-secondary text-mmd">{{ pass.longitude }}</span>
                  </div>
                  <div class="flex flex-col">
                      <span class="block text-sm text-primary">{{ $t('acquired') }}</span>
                      <span class="block font-sans-secondary text-mmd">{{ pass.time }}</span>
                  </div>
              </div>
              <div class="grid grid-cols-2 gap-5 xs:gap-10" v-if="!loading">
                  <div class="flex flex-col" :class="pass.dtaCode ? 'col-span-2 xs:col-span-1' : '' ">
                      <span class="block text-sm text-primary">DTA</span>
                      <span class="block font-sans-secondary text-mmd">{{ pass.dtaCode ? pass.dtaCode : 'N/A' }}</span>
                  </div>
                  <div class="flex flex-col" :class="pass.wtpCode ? 'col-span-2 xs:col-span-1' : '' ">
                      <span class="block text-sm text-primary">WTP</span>
                      <span class="block font-sans-secondary text-mmd">{{ pass.wtpCode ? pass.wtpCode : 'N/A' }}</span>
                  </div>
                  <div class="flex flex-col" :class="pass.wtpCode ? 'col-span-2 xs:col-span-1' : '' ">
                      <span class="block text-sm text-primary">Category</span>
                      <span class="block font-sans-secondary text-mmd">{{ pass.consumerCategory }}</span>
                  </div>
                  <div class="flex flex-col" :class="pass.wtpCode ? 'col-span-2 xs:col-span-1' : '' ">
                      <span class="block text-sm text-primary">Ticket Type</span>
                      <span class="block font-sans-secondary text-mmd">{{ pass.productName }}</span>
                  </div>
              </div>
            <!-- <default-button 
              @click="$router.push({name: 'register'})"
              buttonClasses="override text-white"
              :styleOverride="`background-image: linear-gradient(${currentPassBGColour});`"
              :text="$t('pairwith')"
              :icon="fatMap"
            /> -->
          </div>
      </div>


      <!-- <div v-if="editingState" class="px-6 flex flex-col space-y-4 fixed left-0 bottom-6 w-full">
          <default-button 
            @click="updateLifePass"
            buttonClasses="text-primary border border-primary bg-white"
            text="Update LifePass"
          />
      </div> -->
  </div>
</template>

<script>
import moment from 'moment';

import DefaultHeader from "@/components/Header.vue";
import BatteryStatus from "@/components/BatteryStatus.vue";
// import DefaultButton from "@/components/Button.vue";
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'


export default {
  components: {
    DefaultHeader,
    BatteryStatus,
    // DefaultButton,
    PulseLoader
  },
  data() {
    return {
      loading: true,
      editingState: false,
      fatMap: require('@/assets/images/logos/fatmap.png'),
      pass: {},
      passObject: {
        firstName: '',
        lastName: '',
        deviceColour: '',
        mobile: window.localStorage.getItem('usersMobile'),
        authCode: window.localStorage.getItem('auth'),
      },
      passName: 'User Name',
      passNo: this.$route.params.passIndex,
      passesTotal: this.$route.params.totalPasses,
      currentPassBGColour: this.$route.params.colour,
      currentPassFontColour: this.$route.params.fontColour,
      selectableColours: [
          {
            title: 'Pink',
            colour: '#E7328A',
            bgColour: '92.74deg, #AA1A61 2.45%, #E7328A 98.1%, #E7328A 98.1%',
          },
          {
            title: 'Light Blue',
            colour: '#38AAE1',
            bgColour: '92.74deg, #38AAE1 2.45%, #75CBF2 98.1%',
          },
          {
            title: 'Green',
            colour: '#16857F',
            bgColour: '92.74deg, #16857F 2.45%, #16A69D 98.1%',
          },
      ]
    };
  },
  mounted() {
    if(!this.$route.params.id) {
      this.$router.push({ name: 'Dashboard' });
    } else {
      this.getDevice();
      this.scrollToTop();
    }
  },
  methods: {
    getDevice() {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

      this.axios
        .get(`${process.env.VUE_APP_API_BASE_URL}/api/app/device/${this.$route.params.id}?tz=${tz}`)
        .then((res) => {
          this.pass = res;
        }).catch(() => {
          alert('Failed to find device.');
        }).finally(() => {
          this.loading = false;
          if(this.pass.firstName.length > 0) {
            this.passName = this.pass.firstName + ' ' + this.pass.lastName;
          }
        });
    },
    formatDate(date) {
      return moment(date).locale(window.localStorage.getItem('selectedLangShort', name)).format('dddd, DD MMM yy');
    },
    timeAgo(date) {
      return  moment(date).locale(window.localStorage.getItem('selectedLangShort', name)).fromNow(true);
    },
    changeThemeColour(colour) {
      this.passObject.deviceColour = colour.title;
      this.currentPassBGColour = colour.bgColour;
      this.currentPassFontColour = colour.colour;
    },
    toggleEditingState() {
      this.editingState = !this.editingState;
      this.passName = null;
      this.$nextTick(() => {
        this.$refs.passName.focus();
      });
    },
    updateLifePass() {
      // set editing state to false after click.
      this.editingState = false;
      // if statement to split first and lastname out of field
      if(this.passName){
        if(this.passName.trim().indexOf(' ') != -1) {
        var strArr = this.passName.split(' ');
        this.pass.firstName = String(strArr[0]);
        this.passObject.firstName = String(strArr[0]);

        this.pass.lastName = String(strArr[1]);
        this.passObject.lastName = String(strArr[1]);
      } else {
        this.pass.firstName = this.passName;
        this.passObject.firstName = this.passName;
      }
      }


      if(this.passObject.deviceColour == ""){
        this.passObject.deviceColour = this.pass.deviceColour;
      }

      // set Formdata strucutre to pass up all related fields.
      const formData = {
          firstName: this.passObject.firstName,
          lastName: this.passObject.lastName,
          deviceColour: this.passObject.deviceColour,
      }

      // patch request to update the info thats has been changed.
      this.axios
        .patch(`${process.env.VUE_APP_API_BASE_URL}/api/app/device/${this.pass.deviceCode}`, formData)
        .then(() => {
          // alert(res.data)
        }).catch(() => {
          alert('Failed to update device information.');
        }).finally(() => {
          this.loading = false;
        });
    },
    scrollToTop() {
      window.scrollTo(0,0);
    }
  }
};
</script>